// SearchTab.jsx
import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Controls.css';

/*
  SearchTab.jsx

  - Builds absolute PDF URLs that point to files under public/assets.
  - Folder format: "TS <kanda.prashna> Ghanam Sanskrit" (e.g., "TS 4.3 Ghanam Sanskrit").
  - Filename format: uses DOT notation like "4.4.7.2.pdf" (not hyphens).
  - Links open in a new tab. No HEAD checks, no iframe previews.
  - Logs the final absolute URL so you can copy/paste it into the browser for debugging.
*/

const ASSETS_BASE = '/assets';
const DEFAULT_TOPN = 5;

/* ---------- Helpers ---------- */

function normalizeNumericMarker(marker) {
  if (!marker) return null;
  // Accept "4.3.7.2", "4-3-7-2", "4_3_7_2" and return "4.3.7.2"
  const replaced = String(marker).trim().replace(/[_\-]/g, '.');
  const m = replaced.match(/^(\d+(?:\.\d+){0,3})/);
  return m ? m[1] : null;
}

function folderFromMarkerExact(marker) {
  const leading = normalizeNumericMarker(marker);
  if (!leading) return null;
  const parts = leading.split('.');
  const token = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : parts[0];
  return `TS ${token} Ghanam Sanskrit`;
}

// Build filename in DOT form: e.g. "4.4.7.2.pdf"
function filenameDotVariant(sidOrMarker) {
  if (!sidOrMarker) return null;
  const raw = String(sidOrMarker).trim();
  // get leading numeric part if present
  const m = raw.match(/^(\d+(?:[.\-]\d+){0,3})/);
  const base = m ? m[1] : raw.replace(/\s+/g, '');
  // normalize to dots
  const dotted = base.replace(/[_\-]/g, '.');
  // strip trailing (n) suffix if it exists in marker
  const cleaned = dotted.replace(/\(\d+\)$/, '');
  return `${cleaned}.pdf`;
}

function buildPdfUrlFromResult(r) {
  const sid = r.sid || r.marker || r.id || r.markerText;
  if (!sid) return null;

  const folder = folderFromMarkerExact(sid);
  if (!folder) return null;

  const filename = filenameDotVariant(sid);
  if (!filename) return null;

  // If API provided explicit URL and it's a PDF, we still prefer our canonical layout,
  // but if explicit has '/fuzzy/' we'll map it to this folder.
  const explicit = r.pdf || r.sourceFile || r.source_pdf || r.source;
  if (explicit && typeof explicit === 'string') {
    try {
      const explicitUrl = new URL(explicit, window.location.origin);
      if (/\/fuzzy(\/|$)/i.test(explicitUrl.pathname)) {
        const newPath = `${ASSETS_BASE}/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`;
        const final = `${explicitUrl.origin}${newPath}`;
        console.log('Resolved explicit fuzzy ->', final);
        return final;
      }
      if (explicit.toLowerCase().endsWith('.pdf')) {
        console.log('Using explicit PDF URL:', explicitUrl.toString());
        return explicitUrl.toString();
      }
    } catch (e) {
      // ignore and fall back
    }
  }

  const relative = `${ASSETS_BASE}/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`;
  const absolute = window.location.origin + (relative.startsWith('/') ? relative : '/' + relative);
  console.log('Built PDF URL:', absolute);
  return absolute;
}

function buildKramamPdfUrlFromResult(r) {
  const sid = r.sid || r.marker || r.id || r.markerText;
  if (!sid) return null;

  const normalized = normalizeNumericMarker(sid);
  if (!normalized) return null;

  const parts = normalized.split('.');
  if (parts.length < 2) return null;

  // Folder format: TS{kanda}{prashna}_split
  const kanda = parts[0];
  const prashna = parts[1];
  const folder = `TS${kanda}${prashna}_split`;

  // File format: TS_{kanda}_{prashna}_{anuvaka}_{sentence}_Kramam.pdf
  const filename = `TS_${parts.join('_')}_Kramam.pdf`;

  const relative = `/assets/kramam/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`;
  const absolute = window.location.origin + relative;
  console.log('Built Kramam PDF URL:', absolute);
  return absolute;
}

function buildJataiPdfUrlFromResult(r) {
  const sid = r.sid || r.marker || r.id || r.markerText;
  if (!sid) return null;

  const normalized = normalizeNumericMarker(sid);
  if (!normalized) return null;

  const parts = normalized.split('.');
  if (parts.length < 2) return null;

  // Folder format: TS{kanda}{prashna}_split (e.g., TS11_split, TS12_split, etc.)
  const kanda = parts[0];
  const prashna = parts[1];
  const folder = `TS${kanda}${prashna}_split`;

  // File format: TS_Jatai_1_1_1_1.pdf
  const filename = `TS_Jatai_${parts.join('_')}.pdf`;

  const relative = `/assets/jatai/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`;
  const absolute = window.location.origin + relative;
  console.log('Built Jatai PDF URL:', absolute);
  return absolute;
}

function buildPadamPdfUrlFromResult(r) {
  const sid = r.sid || r.marker || r.id || r.markerText;
  if (!sid) return null;

  const normalized = normalizeNumericMarker(sid);
  if (!normalized) return null;

  const parts = normalized.split('.');
  if (parts.length < 2) return null;

  // Folder format: split_TS{kanda}{prashna}
  const kanda = parts[0];
  const prashna = parts[1];
  const folder = `split_TS${kanda}${prashna}`;

  // File format: TS_{kanda}.{prashna}.{anuvaka}.{sentence}.pdf
  const filename = `TS_${parts.join('.')}.pdf`;

  const relative = `/assets/padam/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}`;
  const absolute = window.location.origin + relative;
  console.log('Built Padam PDF URL:', absolute);
  return absolute;
}

/* ---------- UI components ---------- */

const ResultItem = React.memo(function ResultItem({ r, query, highlightTokens }) {
  const pdfUrl = useMemo(() => buildPdfUrlFromResult(r), [r]);
  const padamUrl = useMemo(() => buildPadamPdfUrlFromResult(r), [r]);
  const jataiUrl = useMemo(() => buildJataiPdfUrlFromResult(r), [r]);
  const kramamUrl = useMemo(() => buildKramamPdfUrlFromResult(r), [r]);

  const highlightedSnippet = useMemo(() => {
    return { __html: highlightTokens(r.text, query) };
  }, [r.text, query, highlightTokens]);

  return (
    <div className="result-item" style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{r.marker}</div>
          <div className="snippet" dangerouslySetInnerHTML={highlightedSnippet} />
        </div>

        <div style={{ minWidth: 160, textAlign: 'right' }}>
          {r.score != null && <div style={{ marginBottom: 8 }}>{(r.score * 100).toFixed(0)}%</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {padamUrl ? (
              <a
                href={padamUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '6px 10px',
                  background: '#6f42c1',
                  color: '#fff',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 14,
                  textAlign: 'center'
                }}
              >
                Open Padam and Samhita
              </a>
            ) : (
              <div style={{ color: '#999', fontSize: 13 }}>Padam PDF not available</div>
            )}

            {kramamUrl ? (
              <a
                href={kramamUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '6px 10px',
                  background: '#28a745',
                  color: '#fff',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 14,
                  textAlign: 'center'
                }}
              >
                Open Kramam
              </a>
            ) : (
              <div style={{ color: '#999', fontSize: 13 }}>Kramam PDF not available</div>
            )}

            {jataiUrl ? (
              <a
                href={jataiUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '6px 10px',
                  background: '#d63384',
                  color: '#fff',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 14,
                  textAlign: 'center'
                }}
              >
                Open Jatai
              </a>
            ) : (
              <div style={{ color: '#999', fontSize: 13 }}>Jatai PDF not available</div>
            )}

            {pdfUrl ? (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '6px 10px',
                  background: '#0078d4',
                  color: '#fff',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 14,
                  textAlign: 'center'
                }}
              >
                Open Ghanam
              </a>
            ) : (
              <div style={{ color: '#999', fontSize: 13 }}>Ghanam PDF not available</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        {r.original_text && <p className="sanskrit-text" style={{ margin: '6px 0' }}>{r.original_text}</p>}
        {r.english && (
          <p
            className="english-text"
            style={{ margin: '6px 0', color: '#333' }}
            dangerouslySetInnerHTML={{ __html: highlightTokens(r.english, r.text) }}
          />
        )}
      </div>
    </div>
  );
});

/* ---------- Main SearchTab component ---------- */

function SearchTab() {
  const [section, setSection] = useState('samhita');
  const [query, setQuery] = useState('');
  const [topN, setTopN] = useState(DEFAULT_TOPN);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('phrase');
  const [kanda, setKanda] = useState('');
  const [prashna, setPrashna] = useState('');
  const [anuvaka, setAnuvaka] = useState('');
  const [showImageExpanded, setShowImageExpanded] = useState(false);
  const [listening, setListening] = useState(false);

  const normalize = useCallback((str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const highlightTokens = useCallback((text, qtext) => {
    if (!text) return '';
    if (!qtext) return text;
    const q = normalize(qtext);
    if (!q) return text;
    const regex = new RegExp(q, 'gi');
    return text.replace(regex, m => `<mark className="highlight">${m}</mark>`);
  }, [normalize]);

  const handleSearch = async (inputQuery) => {
    try {
      setLoading(true);
      let res;

      if (mode === 'phrase') {
        const trimmed = String(inputQuery || query || '').trim();
        const cleaned = trimmed.replace(/[^\p{L}\s]/gu, '');
        const wordCount = cleaned.split(/\s+/).filter(Boolean).length;
        const charCount = cleaned.length;

        if (wordCount < 3 && charCount < 15) {
          setError("❌ Please enter at least three words OR at least 15 characters to perform a search.");
          setResults([]);
          setLoading(false);
          return;
        }

        setError('');
        res = await fetch(`/api/search?query=${encodeURIComponent(trimmed)}&topN=${topN}`);
      } else {
        if (!kanda || !prashna || !anuvaka) {
          setError("❌ Please enter Kanda, Prashna, and Anuvaka.");
          setResults([]);
          setLoading(false);
          return;
        }
        setError('');
        res = await fetch(
          `/api/searchByLocation?kanda=${encodeURIComponent(kanda)}&prashna=${encodeURIComponent(prashna)}&anuvaka=${encodeURIComponent(anuvaka)}`
        );
      }

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data || data.length === 0) {
        setError("✨ No results found for the number(s) entered. Please check Kandam, Prashnam, and Anuvakam ✨");
        setResults([]);
      } else {
        setResults(data);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(`⚠️ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  function playBeep() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.1;
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.2);
    } catch (err) {
      console.error('Beep error:', err);
    }
  }

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('🎤 Speech recognition is not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => { setListening(true); setError(''); playBeep(); };
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript || '';
      setQuery(transcript);
      setListening(false);
      handleSearch(transcript);
    };
    recognition.onerror = (event) => { setError(`🎤 Error: ${event.error}`); setListening(false); };
    recognition.onend = () => { setListening(false); };
    recognition.start();
  };

  const overlay = showImageExpanded ? createPortal(
    <div
      className="image-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged Taittirīya overview"
      onClick={() => setShowImageExpanded(false)}
    >
      <div className="overlay-card" onClick={e => e.stopPropagation()}>
        <button
          className="close-image-button"
          type="button"
          onClick={() => setShowImageExpanded(false)}
        >
          ✕
        </button>
        <img
          src="/assets/TaittriyaShaka.png"
          alt="Taittirīya Saṃhita and Brāhmaṇa overview enlarged"
          className="overlay-image"
        />
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {overlay}
      <div className="page-container">
        <div className="content-wrap">
          <h2 className="vedam-heading">कृष्णयजुर्वेदे अन्वेषणम् / Search in Krishna Yajur Vedam</h2>

        <div className="image-preview-row">
          <div
            className="image-preview-card"
            role="button"
            tabIndex={0}
            onClick={() => setShowImageExpanded(true)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                setShowImageExpanded(true);
              }
            }}
          >
            <img
              src="/assets/TaittriyaShaka.png"
              alt="Taittirīya Saṃhita and Brāhmaṇa overview preview"
              className="image-preview"
            />
            <div className="image-preview-caption">Taittirīya overview — click the image to open full size</div>
          </div>
        </div>

        <div className="tab-selector">
          <button
            className={section === 'samhita' ? 'tab active' : 'tab'}
            onClick={() => setSection('samhita')}
          >
            संहिता / Samhita
          </button>
          <button
            className={section === 'brahmana' ? 'tab active' : 'tab'}
            onClick={() => setSection('brahmana')}
          >
            ब्राह्मणम् / Brahmanam
          </button>
        </div>

        {section === 'samhita' && (
          <>
            <div className="search-info">
              <h2>✨ Welcome to the Search Tab</h2>
              <p>
                Our search tool helps you explore the sacred texts with precision and ease.
                You can search in two powerful ways:
              </p>
              <ul>
                <li><strong>Phrase Search</strong>: Enter a mantra or passage — the system highlights the exact words and returns the surrounding context. Searches require at least <em>three words</em> or <em>fifteen characters</em>.</li>
                <li><strong>Location Search</strong>: Navigate directly by <em>Kanda, Prashna, and Anuvaka</em> to view the full section in Sanskrit, transliteration, and translation.</li>
              </ul>
              <p>
                Every result is enriched with a <strong>confidence score</strong>, showing how closely it matches your query.
                Whether you’re tracing a mantra, studying its structure, or exploring the wisdom of the Vedas, this tab brings clarity and depth to your journey.
              </p>
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '8px', borderLeft: '4px solid #0078d4' }}>
                <h4 style={{ color: '#0078d4', marginTop: '0' }}>📐 How This Application Works</h4>
                <p style={{ margin: '8px 0' }}>
                  Curious about how our AI-powered search engine works? Learn about our architecture, embeddings, and vector database:
                </p>
                <a
                  href="/assets/vedagnyaanam_architecture.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '14px 24px',
                    backgroundColor: '#0078d4',
                    color: 'white',
                    borderRadius: '5px',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: '18px'
                  }}
                >
                  📄 View Architecture Diagram (PDF)
                </a>
              </div>            </div>

            <div className="dropdown-block">
              <label className="dropdown-label">अन्वेषणम्  / Search Mode</label>
              <select value={mode} onChange={e => setMode(e.target.value)}>
                <option value="phrase">By Words</option>
                <option value="location">By Kandam/Prashnam/Anuvakam</option>
              </select>
            </div>

            {mode === 'phrase' && (
              <div className="controls-row">
                <input
                  className="big-input"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Enter mantra phrase (≥3 words)"
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(query); }}
                />
                <button className="mic-button" onClick={startListening} disabled={listening}>
                  {listening ? '🎤 Listening...' : '🎤 Speak after the Beep - Beta Mode'}
                </button>
              </div>
            )}

            {mode === 'location' && (
              <div className="location-inputs">
                <div className="location-field">
                  <label className="dropdown-label">Kandam</label>
                  <input className="small-input" value={kanda} onChange={e => setKanda(e.target.value)} />
                </div>
                <div className="location-field">
                  <label className="dropdown-label">Prashnam</label>
                  <input className="small-input" value={prashna} onChange={e => setPrashna(e.target.value)} />
                </div>
                <div className="location-field">
                  <label className="dropdown-label">Anuvakam</label>
                  <input className="small-input" value={anuvaka} onChange={e => setAnuvaka(e.target.value)} />
                </div>
              </div>
            )}

            {mode === 'phrase' && (
              <div className="dropdown-block">
                <label className="dropdown-label">शीर्षफलसंख्या / Top Result count</label>
                <select value={topN} onChange={e => setTopN(Number(e.target.value))}>
                  {Array.from({ length: 25 }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="controls-row">
              <button className="search-button" onClick={() => handleSearch(query)} disabled={loading}>
                Search
              </button>
            </div>

            {loading && <div className="spinner">🔄 Searching...</div>}

            {results.length > 0 && (
              <>
                {/* Summary table */}
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>Kandam–Prashnam–Anuvakam–Sentence</th>
                      <th>Best matched mantra padam(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i}>
                        <td><a href={`#result-${i}`}>{r.marker}</a></td>
                        <td dangerouslySetInnerHTML={{ __html: highlightTokens(r.text, query) }} />
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Full results back to block style */}
                <div className="results-detail">
                  {results.map((r, i) => (
                    <div id={`result-${i}`} key={i} className="result-item">
                      <h4>{r.marker}</h4>
                      
                      <label className="match-label">Best matched mantra padam(s): </label>
                      <strong dangerouslySetInnerHTML={{ __html: highlightTokens(r.text, r.text) }} />

                      <p className="sanskrit-text">{r.original_text}</p>
                      <p className="english-swaras">{r.english_swaras}</p>

                      {mode === 'phrase' && r.english && (
                        <p
                          className="english-text"
                          dangerouslySetInnerHTML={{
                            __html: highlightTokens(r.english, r.text)
                          }}
                        />
                      )}

                      {r.anuvakaSentences && r.anuvakaSentences.length > 0 && (
                        <details className="anuvaka-block" open={mode === 'location'}>
                          <summary>Show full Anuvāka</summary>
                          {r.anuvakaSentences.map((s, j) => (
                            <div key={j} className="anuvaka-sentence">
                              <strong>{s.marker}</strong>
                              <p className="sanskrit-text">{s.original_text}</p>
                              <p className="english-swaras">{s.english_swaras}</p>
                              <p
                                className="english-text"
                                dangerouslySetInnerHTML={{
                                  __html: highlightTokens(s.english, r.text)
                                }}
                              />
                            </div>
                          ))}
                        </details>
                      )}
                      {/* Open Ghanam and Kramam links */}
                      {(() => {
                        const pdfUrl = buildPdfUrlFromResult(r);
                        const kramamUrl = buildKramamPdfUrlFromResult(r);
                        const padamUrl = buildPadamPdfUrlFromResult(r);
                        const jataiUrl = buildJataiPdfUrlFromResult(r);
                        return (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 20 }}>
                            {padamUrl ? (
                              <a
                                href={padamUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '6px 10px',
                                  background: '#6f42c1',
                                  color: '#fff',
                                  borderRadius: 6,
                                  textDecoration: 'none',
                                  fontSize: 14
                                }}
                              >
                                Open Padam and Samhita
                              </a>
                            ) : (
                              <div style={{ color: '#999', fontSize: 13 }}>
                                Padam PDF not available
                              </div>
                            )}

                            {kramamUrl ? (
                              <a
                                href={kramamUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '6px 10px',
                                  background: '#28a745',
                                  color: '#fff',
                                  borderRadius: 6,
                                  textDecoration: 'none',
                                  fontSize: 14
                                }}
                              >
                                Open Kramam
                              </a>
                            ) : (
                              <div style={{ color: '#999', fontSize: 13 }}>
                                Kramam PDF not available
                              </div>
                            )}

                            {jataiUrl ? (
                              <a
                                href={jataiUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '6px 10px',
                                  background: '#d63384',
                                  color: '#fff',
                                  borderRadius: 6,
                                  textDecoration: 'none',
                                  fontSize: 14
                                }}
                              >
                                Open Jatai
                              </a>
                            ) : (
                              <div style={{ color: '#999', fontSize: 13 }}>
                                Jatai PDF not available
                              </div>
                            )}

                            {pdfUrl ? (
                              <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '6px 10px',
                                  background: '#0078d4',
                                  color: '#fff',
                                  borderRadius: 6,
                                  textDecoration: 'none',
                                  fontSize: 14
                                }}
                              >
                                Open Ghanam
                              </a>
                            ) : (
                              <div style={{ color: '#999', fontSize: 13 }}>
                                Ghanam PDF not available
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </>
            )}

            {error && <div className="no-results">{error}</div>}
          </>
        )}

        {section === 'brahmana' && (
          <div className="brahmana-placeholder">
            <p>📖 Brahmanan search will be available shortly. Stay tuned!</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default SearchTab;